import React, { useMemo, useState } from "react";
import { Coins, Cpu, Users, Zap, Wallet, Download, Check, AlertCircle } from "lucide-react";
import { usePaged, TablePager } from "@/components/dashboard/Pager";
import { useCollapse, CollapseToggle } from "@/components/dashboard/Collapse";

/* Every number CaryPact gives us, wallet by wallet, with a total for whatever
   you tick. Columns are grouped by CaryPact's own screens and each one carries a
   single unit, so a column total is always apples to apples. */

const CA = "#C2185B";
const USDT = "#0B8F78";
const BOT = "#1F8A4C";
const TEAM = "#7C3AED";
const MINING = "#8D1CEB";

const num = (v, dp = 2) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
const thin = (v, dp = 2) => (Number(v || 0) === 0 ? "—" : num(v, dp));

// Grouped exactly as CaryPact lays them out, so a number here can be checked there.
const GROUPS = [
  {
    key: "withdrawable", label: "Ready to withdraw", icon: Zap, colour: "#B45309",
    note: "Balances sitting in CaryPact right now, waiting for you to take them out",
    cols: [
      { key: "bot_reward", label: "BOT mining", unit: "BOT", dp: 4, colour: BOT, needs: "has_bot_data",
        help: "CaryPact · BOT Mining — available balance (static mining + VIP level dividend)" },
      { key: "bot_power_reward", label: "Hashrate revenue", unit: "BOT", dp: 6, colour: BOT, needs: "has_power_data",
        help: "CaryPact · Buy computing power → Hashrate Mining Revenue — withdrawable earnings. Paid in BOT (its income account says assetType BOT)" },
      { key: "static_ca", label: "CA mining", unit: "CA", dp: 8, colour: MINING,
        help: "CaryPact · CA mining — the withdrawable earnings figure you tap Withdraw on (staticProfitCaAmount). It's a balance, not lifetime income" },
      { key: "dynamic_ca", label: "Team profit", unit: "CA", dp: 6, colour: TEAM,
        help: "CaryPact · dynamicProfitCaAmount — team/dynamic earnings sitting in the account, also a balance not a lifetime total" },
      { key: "staking_rewards_ca", label: "Staking rewards", unit: "CA", dp: 4, colour: CA,
        help: "CaryPact · Staking — reward earned on your positions and not yet collected" },
      { key: "spare_ca", label: "Spare", unit: "CA", dp: 4, colour: CA,
        help: "Principal a finished term has released — sitting in the account doing nothing" },
      { key: "community_reward_ca", label: "Team reward", unit: "CA", dp: 4, colour: TEAM,
        help: "CaryPact · Community reward for the current period" },
      { key: "bot_power_settling", label: "Settling", unit: "BOT", dp: 6, colour: "#0EA5E9", needs: "has_power_data",
        help: "Hashrate earnings CaryPact is still settling — not withdrawable yet" },
    ],
  },
  {
    key: "staking", label: "Staked & locked", icon: Coins, colour: CA,
    note: "Your capital at work inside CaryPact",
    cols: [
      { key: "staked_ca", label: "Staked", unit: "CA", dp: 2, colour: CA, help: "CaryPact · Staking — principal on this wallet" },
      { key: "locked_ca", label: "Locked", unit: "CA", dp: 2, colour: CA, help: "CaryPact · totalLockCaAmount" },
      { key: "mining_usdt", label: "Locked", unit: "USDT", dp: 2, colour: USDT, help: "CaryPact · totalLockUsdtAmount — USDT put into mining" },
      { key: "locked_bot", label: "Locked", unit: "BOT", dp: 2, colour: BOT, help: "CaryPact · totalLockBotAmount" },
      { key: "flexible_ca", label: "Flexible", unit: "CA", dp: 2, colour: CA, help: "Staked with no fixed term — you can pull it out any time" },
    ],
  },
  {
    key: "mining", label: "CA mining", icon: Cpu, colour: MINING,
    note: "What the USDT you put into mining has produced",
    cols: [
      { key: "gained_usdt", label: "Gained", unit: "USDT", dp: 2, colour: USDT, help: "CaryPact · gainedUsdtAmount" },
      { key: "power", label: "Power", unit: "", dp: 2, colour: MINING, help: "CaryPact · totalPowerAmount — your own hashpower value" },
      { key: "miner_usd", label: "Miner performance", unit: "USDT", dp: 0, colour: USDT, help: "CaryPact · minerPerformanceAmount" },
    ],
  },
  {
    key: "bot", label: "BOT mining", icon: Zap, colour: BOT, needs: "has_bot_data",
    note: "Static mining plus the VIP level dividend, paid in BOT",
    cols: [
      { key: "bot_reward_total", label: "Earned", unit: "BOT", dp: 4, colour: BOT, help: "Lifetime BOT mining income" },
      { key: "bot_withdrawn", label: "Withdrawn", unit: "BOT", dp: 4, colour: BOT, help: "BOT already taken out" },
      { key: "bot_static_total", label: "Static", unit: "BOT", dp: 4, colour: BOT, help: "The static-mining share of that income" },
      { key: "bot_dividend_total", label: "VIP dividend", unit: "BOT", dp: 4, colour: BOT,
        help: "The level-dividend share. CaryPact also calls this Hashrate Mining on the BOT_B account — same money, shown once here" },
      { key: "bot_valid_miners", label: "Valid miners", unit: "", dp: 0, colour: BOT, help: "CaryPact · botMiningValidMiners" },
    ],
  },
  {
    key: "hashpower", label: "Bought hashrate", icon: Cpu, colour: "#0EA5E9", needs: "has_power_data",
    note: "CaryPact's Buy computing power product",
    cols: [
      { key: "bot_power_hashrate", label: "My hashrate", unit: "", dp: 2, colour: "#0EA5E9", help: "CaryPact · Buy computing power → my hashrate" },
      { key: "bot_power_total", label: "Earned", unit: "BOT", dp: 6, colour: BOT, help: "Lifetime hashrate revenue, paid in BOT" },
      { key: "bot_power_withdrawn", label: "Withdrawn", unit: "BOT", dp: 6, colour: BOT, help: "Hashrate revenue already taken out" },
    ],
  },
  {
    key: "team", label: "Team & community", icon: Users, colour: TEAM,
    note: "What sits beneath this wallet, and what it pays",
    cols: [
      { key: "team_lock_usd", label: "Team locked", unit: "USDT", dp: 0, colour: USDT, help: "CaryPact · teamLockUsdtAmount — the same Downline $ the org tree shows" },
      { key: "team_lock_ca", label: "Team locked", unit: "CA", dp: 0, colour: CA, help: "CaryPact · teamLockCaAmount" },
      { key: "team_power", label: "Team power", unit: "", dp: 0, colour: TEAM, help: "CaryPact · teamPowerAmount" },
      { key: "community_reward_last_ca", label: "Last reward", unit: "CA", dp: 4, colour: TEAM, help: "CaryPact · community reward for the previous period" },
      { key: "invite_count", label: "Direct", unit: "", dp: 0, colour: TEAM, help: "Wallets invited directly by this one" },
      { key: "team_count", label: "Team", unit: "", dp: 0, colour: TEAM, help: "Everyone beneath this wallet" },
    ],
  },
];

const Unit = ({ children, colour }) => (
  <span className="text-[9.5px] font-semibold ml-0.5" style={{ color: colour, opacity: 0.75 }}>{children}</span>
);

export const WalletLedger = ({ wallets = [], caPrice = 0 }) => {
  const [open, setOpen] = useState(() => GROUPS.map((g) => g.key));
  const [picked, setPicked] = useState([]);
  const [sort, setSort] = useState({ key: "staked_ca", dir: -1 });

  const all = GROUPS;
  const groups = useMemo(() => all.filter((g) => open.includes(g.key)), [all, open]);
  const cols = useMemo(
    () => groups.flatMap((g) => g.cols.map((c) => ({ ...c, group: g.key, needs: c.needs || g.needs }))),
    [groups]);
  // CaryPact hands over the mining accounts only for the wallet you connected as,
  // so for every other wallet those columns are unknown — never zero.
  const known = (w, c) => !c.needs || !!w[c.needs];

  const rows = useMemo(() => {
    const list = [...wallets].map((w) => ({ ...w, read: !!w.cp_synced_at }));
    return list.sort((a, b) => {
      const v = (Number(b[sort.key]) || 0) - (Number(a[sort.key]) || 0);
      return sort.dir === -1 ? v : -v;
    });
  }, [wallets, sort]);

  // Nothing ticked means "all of them" — the total should never read zero just
  // because you haven't chosen yet.
  const counted = picked.length ? rows.filter((w) => picked.includes(w.address)) : rows;
  const totals = useMemo(() => {
    const t = {};
    cols.forEach((c) => {
      t[c.key] = counted.reduce((a, w) => a + (known(w, c) ? Number(w[c.key]) || 0 : 0), 0);
      t[`${c.key}__unread`] = counted.filter((w) => w.read && !known(w, c)).length;
    });
    return t;
  }, [counted, cols]);

  // A community has hundreds of wallets — the rows start hidden and the totals
  // still read on every wallet, so nothing is lost by keeping it shut.
  const { open: rowsOpen, toggle: toggleRows } = useCollapse("wallet-ledger", rows.length);
  const pager = usePaged(rows, 25);

  const toggle = (addr) =>
    setPicked((p) => (p.includes(addr) ? p.filter((x) => x !== addr) : [...p, addr]));

  const csv = () => {
    const head = ["Wallet", "Address", "Read from CaryPact",
      ...cols.map((c) => `${c.label}${c.unit ? ` (${c.unit})` : ""}`)];
    const body = counted.map((w) => [w.name, w.address, w.cp_synced_at || "never",
      ...cols.map((c) => w[c.key] ?? 0)]);
    body.push(["TOTAL", `${counted.length} wallets`, "", ...cols.map((c) => totals[c.key])]);
    const blob = new Blob([[head, ...body].map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n")],
      { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `carypact-wallets-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!wallets.length) return null;
  const unread = rows.filter((w) => !w.read).length;

  return (
    <div className="glass p-0 overflow-hidden" data-testid="wallet-ledger">
      <div className="px-5 pt-5 pb-3 flex items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Wallet size={15} style={{ color: CA }} />
            <h3 className="font-display text-lg font-semibold tracking-tight">Every CaryPact number, wallet by wallet</h3>
          </div>
          <p className="text-[12.5px] text-ink-3 mt-1">
            Tick the wallets you want and the totals follow. Each column keeps its own unit, so nothing
            is added together that shouldn't be.
            {unread > 0 && (
              <span style={{ color: "#B45309" }}> {unread} wallet{unread === 1 ? " has" : "s have"} never
                been synced — shown as a dash, not a zero.</span>
            )}
            <span className="block mt-1">
              <b style={{ color: "#B45309" }}>n/r</b> means CaryPact never reported that figure for that
              wallet. It only hands over the BOT mining and bought-hashrate accounts for the wallet whose
              connection you used, so those columns are only filled in for wallets you've connected as —
              anything else would be a made-up zero.
            </span>
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11.5px] text-ink-3" data-testid="wallet-ledger-count">
            {picked.length ? `${picked.length} of ${rows.length} ticked` : `all ${rows.length} wallets`}
          </span>
          {!!picked.length && (
            <button type="button" onClick={() => setPicked([])} className="btn-secondary !py-1.5 !text-[12px]"
              data-testid="wallet-ledger-clear">Clear</button>
          )}
          <button type="button" onClick={csv} className="btn-secondary !py-1.5 !text-[12px]"
            data-testid="wallet-ledger-csv"><Download size={12} /> CSV</button>
          <CollapseToggle open={rowsOpen} onClick={toggleRows} count={rows.length}
            testId="wallet-ledger-collapse" />
        </div>
      </div>

      <div className="px-5 pb-3 flex items-center gap-1.5 flex-wrap">
        {all.map((g) => {
          const on = open.includes(g.key);
          const Icon = g.icon;
          return (
            <button key={g.key} type="button" title={g.note}
              onClick={() => setOpen((o) => (on ? o.filter((x) => x !== g.key) : [...o, g.key]))}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11.5px] font-semibold transition-colors"
              style={on ? { background: `${g.colour}14`, color: g.colour }
                        : { background: "rgba(15,18,38,0.04)", color: "rgba(15,18,38,0.45)" }}
              data-testid={`wallet-ledger-group-${g.key}`}>
              <Icon size={12} /> {g.label}
              {on && <Check size={11} />}
            </button>
          );
        })}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="wallet-ledger-table">
          <thead>
            <tr style={{ background: "rgba(15,18,38,0.03)" }}>
              <th className="text-left px-3 py-2 sticky left-0 z-10" style={{ background: "#F7F7FB", minWidth: 190 }}>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="wallet-ledger-all"
                    checked={picked.length === rows.length && rows.length > 0}
                    onChange={(e) => setPicked(e.target.checked ? rows.map((w) => w.address) : [])} />
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">Wallet</span>
                </label>
              </th>
              {groups.map((g) =>
                g.cols.map((c, i) => (
                  <th key={`${g.key}-${c.key}-${i}`} title={c.help}
                    onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : -1 }))}
                    className="text-right px-3 py-2 whitespace-nowrap cursor-pointer select-none"
                    style={{ borderLeft: i === 0 ? `2px solid ${g.colour}26` : undefined }}>
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em]"
                      style={{ color: sort.key === c.key ? c.colour : "rgba(15,18,38,0.5)" }}>
                      {c.label}{c.unit ? <Unit colour={c.colour}>{c.unit}</Unit> : null}
                    </span>
                  </th>
                )))}
            </tr>
          </thead>
          <tbody>
            {!rowsOpen ? (
              <tr data-testid="wallet-ledger-collapsed">
                <td colSpan={1 + cols.length} className="px-3 py-3 text-[12px] text-ink-3"
                  style={{ borderTop: "1px solid rgba(15,18,38,0.05)" }}>
                  {rows.length} wallet{rows.length === 1 ? "" : "s"} hidden — the totals below still
                  count every one of them. Tap <b>Show {rows.length} wallets</b> to open the list.
                </td>
              </tr>
            ) : pager.pageItems.map((w) => {
              const on = picked.includes(w.address);
              return (
                <tr key={w.address || w.node_id} data-testid={`wallet-ledger-row-${w.address}`}
                  onClick={() => toggle(w.address)}
                  className="cursor-pointer transition-colors"
                  style={{ borderTop: "1px solid rgba(15,18,38,0.05)",
                           background: on ? "rgba(194,24,91,0.045)" : undefined }}>
                  <td className="px-3 py-2 sticky left-0 z-10"
                    style={{ background: on ? "#FBEFF4" : "#FDFDFE", minWidth: 190 }}>
                    <label className="inline-flex items-center gap-2 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={on} onChange={() => toggle(w.address)}
                        data-testid={`wallet-ledger-pick-${w.address}`} />
                      <span className="min-w-0">
                        <span className="block font-semibold truncate max-w-[150px]">{w.name || "—"}</span>
                        <span className="block text-[10.5px] text-ink-3">
                          {w.read
                            ? `${w.cp_fields || 0} fields · ${String(w.cp_synced_at).slice(0, 10)}`
                            : <span style={{ color: "#B45309" }} className="inline-flex items-center gap-1">
                                <AlertCircle size={9} /> never read
                              </span>}
                        </span>
                      </span>
                    </label>
                  </td>
                  {groups.map((g) =>
                    g.cols.map((c, i) => (
                      <td key={`${g.key}-${c.key}-${i}`} className="px-3 py-2 text-right num whitespace-nowrap"
                        style={{ borderLeft: i === 0 ? `2px solid ${g.colour}14` : undefined,
                                 color: (Number(w[c.key]) || 0) > 0 ? c.colour : "rgba(15,18,38,0.28)",
                                 fontWeight: (Number(w[c.key]) || 0) > 0 ? 600 : 400 }}>
                        {!w.read
                          ? <span title="This wallet has never been synced from CaryPact">—</span>
                          : known(w, c)
                            ? thin(w[c.key], c.dp)
                            : <span title="CaryPact only reports this for the wallet you connect as — this one has never been read, so we won't show a zero"
                                style={{ color: "#B45309", opacity: 0.75 }}>n/r</span>}
                      </td>
                    )))}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background: "rgba(15,18,38,0.04)", borderTop: "2px solid rgba(15,18,38,0.1)" }}>
              <td className="px-3 py-2.5 sticky left-0 z-10 font-semibold"
                style={{ background: "#F2F2F7", minWidth: 190 }} data-testid="wallet-ledger-total-label">
                {picked.length ? `${picked.length} wallet${picked.length === 1 ? "" : "s"} ticked` : `All ${rows.length} wallets`}
              </td>
              {groups.map((g) =>
                g.cols.map((c, i) => (
                  <td key={`${g.key}-${c.key}-${i}`} data-testid={`wallet-ledger-total-${c.key}`}
                    className="px-3 py-2.5 text-right num font-bold whitespace-nowrap"
                    style={{ borderLeft: i === 0 ? `2px solid ${g.colour}26` : undefined,
                             color: totals[c.key] > 0 ? c.colour : "rgba(15,18,38,0.3)" }}>
                    {thin(totals[c.key], c.dp)}
                    {totals[`${c.key}__unread`] > 0 && (
                      <span className="block text-[9.5px] font-semibold" style={{ color: "#B45309" }}>
                        +{totals[`${c.key}__unread`]} n/r
                      </span>
                    )}
                  </td>
                )))}
            </tr>
          </tfoot>
        </table>
      </div>

      {rowsOpen && <TablePager pager={pager} testId="wallet-ledger-pager" label="wallets" />}

      <div className="px-5 py-3 text-[11px] text-ink-3" style={{ borderTop: "1px solid rgba(15,18,38,0.06)" }}>
        Hover any column heading to see exactly which CaryPact screen it comes from. Click one to sort.
        {caPrice > 0 && <> CA is ${Number(caPrice).toFixed(2)} right now.</>}
      </div>
    </div>
  );
};

export default WalletLedger;
